/**
 * Yards & zones — `organization-build-book.md` §6.9, `org-09-yards-zones.png`.
 *
 * The two things a branch owns that are places rather than people: the yard
 * stock leaves from, and the zones properties are routed by. Both live on a
 * branch and only on a branch — a region has no loading dock — so every other
 * kind gets the sentence and a list of its branches instead of an empty tab.
 *
 * Neither is ever deleted (rule 2). A yard that stock has moved through stays
 * on the record for good; a zone with properties in it is switched off rather
 * than removed, which leaves the properties' history true.
 */
import { useState } from 'react'
import type { OrgUnitDetail, OrgUnitRow, YardView, ZoneView } from '@rowted/shared'
import { ZONE_COLOURS } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { request } from '../../lib/api.js'
import { formatCents, formatNumber } from '../../lib/format.js'
import { KindTile } from '../../builder/kind-tile.js'
import { Button, Tag } from '../primitives.js'
import { Sheet } from '../sheet.js'
import {
  AddressFields, Field, SwatchField, SwitchRow, TextArea, TextInput,
  type AddressValue,
} from '../form/index.js'
import { IconMaps, IconPlus } from '../../frame/icons.js'

const blankAddress = (): AddressValue => ({ line1: '', line2: '', city: '', state: '', postalCode: '' })

export function OrgYards({ unit, units, canEdit, onSaved, onSelect }: {
  unit: OrgUnitDetail
  units: OrgUnitRow[]
  canEdit: boolean
  onSaved: (unit: OrgUnitDetail) => void
  onSelect: (id: string) => void
}) {
  const [yards, setYards] = useState<YardView[]>(unit.yards)
  const [zones, setZones] = useState<ZoneView[]>(unit.zones)
  const [editingYard, setEditingYard] = useState<YardView | 'new' | null>(null)
  const [editingZone, setEditingZone] = useState<ZoneView | 'new' | null>(null)

  // A different branch is a different tab's worth of rows.
  const [shownFor, setShownFor] = useState(unit.id)
  if (shownFor !== unit.id) {
    setShownFor(unit.id)
    setYards(unit.yards)
    setZones(unit.zones)
  }

  if (unit.kind !== 'branch') {
    const branches = units.filter((one) => one.kind === 'branch' && reaches(units, unit.id, one.id))
    return (
      <div className="flex flex-col gap-2.5 px-[18px] py-4">
        <p className="m-0 text-[13px] text-text">Yards and zones live on branches.</p>
        {branches.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {branches.map((branch) => (
              <button
                key={branch.id}
                type="button"
                onClick={() => onSelect(branch.id)}
                className={cn('flex items-center gap-1.5 rounded-chip border border-line',
                  'bg-surface px-2.5 py-1 text-[12.5px] font-semibold text-text hover:bg-hover')}
              >
                <KindTile kind="branch" size={18} />
                {branch.name}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <Section
        title="Yards"
        sub="where inventory lives and crews start their day"
        action={canEdit && (
          <Button className="h-8 text-[12.5px]" onClick={() => setEditingYard('new')}>
            <IconPlus size={15} />Add yard
          </Button>
        )}
      >
        {yards.length === 0 && <Blank>No yard yet — a branch needs one to close a visit out.</Blank>}
        {yards.map((yard) => (
          <div key={yard.id} className="flex items-center gap-3 border-b border-line-faint px-[18px] py-2.5 last:border-b-0">
            <KindTile kind="yard" size={32} />
            <span className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="flex items-center gap-2">
                <b className="truncate text-[13.5px] text-text">{yard.name}</b>
                {yard.isDefault && <Tag tone="ok">Default yard</Tag>}
              </span>
              <span className="truncate text-[12px] text-text-muted">
                {[yard.addressLine1, yard.note].filter(Boolean).join(' · ') || 'No address yet'}
              </span>
            </span>
            <span className="shrink-0 text-[12.5px] text-text-muted max-md:hidden">
              {yardMeta(yard)}
            </span>
            {canEdit && (
              <Button className="h-[30px] shrink-0 text-[12px]" onClick={() => setEditingYard(yard)}>
                Edit
              </Button>
            )}
          </div>
        ))}
      </Section>

      <Section
        title="Service zones"
        sub="group properties for routing and the schedule board"
        action={canEdit && (
          <Button className="h-8 text-[12.5px]" onClick={() => setEditingZone('new')}>
            <IconPlus size={15} />Add zone
          </Button>
        )}
      >
        {zones.length === 0 && <Blank>No zones yet — properties route by branch until there are.</Blank>}
        {zones.map((zone) => (
          <div key={zone.id} className="flex items-center gap-3 border-b border-line-faint px-[18px] py-2.5 last:border-b-0">
            <span
              aria-hidden
              style={{ background: zone.colour }}
              className={cn('h-[14px] w-[14px] shrink-0 rounded-[4px]', !zone.active && 'opacity-40')}
            />
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <b className={cn('truncate text-[13.5px] text-text', !zone.active && 'opacity-55')}>
                {zone.name}
              </b>
              {!zone.active && <Tag>Off</Tag>}
            </span>
            <span className="shrink-0 text-[12.5px] text-text-muted max-md:hidden">
              {zoneMeta(zone)}
            </span>
            {canEdit && (
              <Button className="h-[30px] shrink-0 text-[12px]" onClick={() => setEditingZone(zone)}>
                Edit
              </Button>
            )}
          </div>
        ))}
      </Section>

      <div className={cn('flex items-start gap-2 border-t border-line-faint px-[18px] py-3',
        'text-[12px] leading-[1.45] text-text-muted')}>
        <span className="flex shrink-0 pt-px text-text-faint"><IconMaps size={14} /></span>
        <span>
          Draw or edit zone boundaries under <b className="font-semibold">Scheduling → Maps &amp;
          routing</b>; properties fall into a zone by address.
        </span>
      </div>

      {editingYard && (
        <YardSheet
          unitId={unit.id}
          yard={editingYard === 'new' ? null : editingYard}
          onClose={() => setEditingYard(null)}
          onDone={(items) => { setYards(items); setEditingYard(null); void refresh(unit.id, onSaved) }}
        />
      )}

      {editingZone && (
        <ZoneSheet
          unitId={unit.id}
          zone={editingZone === 'new' ? null : editingZone}
          onClose={() => setEditingZone(null)}
          onDone={(items) => { setZones(items); setEditingZone(null); void refresh(unit.id, onSaved) }}
        />
      )}
    </div>
  )
}

/** The counts on the card's header move with the rows, so re-read the unit. */
async function refresh(id: string, onSaved: (unit: OrgUnitDetail) => void) {
  try {
    onSaved(await request<OrgUnitDetail>(`/org-units/${id}`))
  } catch { /* the rows are already right; the chip catches up on the next open */ }
}

function Section({ title, sub, action, children }: {
  title: string; sub: string; action?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <>
      <div className="flex items-baseline gap-2 px-[18px] pb-1.5 pt-3">
        <b className="text-[13px] text-text">{title}</b>
        <span className="min-w-0 truncate text-[12px] text-text-muted">{sub}</span>
        {action && <span className="ml-auto shrink-0 self-center">{action}</span>}
      </div>
      <div className="flex flex-col border-y border-line-faint">{children}</div>
    </>
  )
}

const Blank = ({ children }: { children: React.ReactNode }) => (
  <p className="m-0 px-[18px] py-3 text-[12.5px] text-text-faint">{children}</p>
)

function yardMeta(yard: YardView): string {
  const parts: string[] = []
  parts.push(`${formatNumber(yard.itemsOnHand)} item${yard.itemsOnHand === 1 ? '' : 's'} on hand`)
  if (yard.crews > 0) parts.push(`${yard.crews} crew${yard.crews === 1 ? '' : 's'} start here`)
  if (yard.stockValueCents > 0) parts.push(`${formatCents(yard.stockValueCents)} stock`)
  return parts.join(' · ')
}

function zoneMeta(zone: ZoneView): string {
  const parts = [`${formatNumber(zone.properties)} propert${zone.properties === 1 ? 'y' : 'ies'}`]
  if (zone.crews > 0) parts.push(`${zone.crews} crew${zone.crews === 1 ? '' : 's'}`)
  return parts.join(' · ')
}

/** Whether `unitId` is somewhere above `branchId`. */
function reaches(units: OrgUnitRow[], unitId: string, branchId: string): boolean {
  const byId = new Map(units.map((one) => [one.id, one]))
  let at = byId.get(branchId)?.parentId ?? null
  while (at) {
    if (at === unitId) return true
    at = byId.get(at)?.parentId ?? null
  }
  return false
}

// ---------------------------------------------------------------------------

function YardSheet({ unitId, yard, onClose, onDone }: {
  unitId: string
  yard: YardView | null
  onClose: () => void
  onDone: (items: YardView[]) => void
}) {
  const [name, setName] = useState(yard?.name ?? '')
  const [address, setAddress] = useState<AddressValue>(yard
    ? {
      line1: yard.addressLine1 ?? '', line2: '', city: yard.city ?? '',
      state: yard.state ?? '', postalCode: yard.postalCode ?? '',
    }
    : blankAddress())
  const [isDefault, setDefault] = useState(yard?.isDefault ?? false)
  const [note, setNote] = useState(yard?.note ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const { items } = await request<{ items: YardView[] }>(
        yard ? `/org-units/${unitId}/yards/${yard.id}` : `/org-units/${unitId}/yards`,
        {
          method: yard ? 'PUT' : 'POST',
          body: {
            name,
            addressLine1: address.line1 || null,
            city: address.city || null,
            state: address.state || null,
            postalCode: address.postalCode || null,
            latitude: address.latitude ?? null,
            longitude: address.longitude ?? null,
            isDefault,
            note,
          },
        })
      onDone(items)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  async function retire() {
    if (!yard) return
    setBusy(true)
    setError(null)
    try {
      const { items } = await request<{ items: YardView[] }>(
        `/org-units/${unitId}/yards/${yard.id}`, { method: 'DELETE' })
      onDone(items)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That yard stays.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      kind="yard"
      title={yard ? yard.name : 'New yard'}
      sub="where inventory lives and crews start their day"
      width={480}
      dirty
      saveLabel={yard ? 'Save yard' : 'Add yard'}
      onSave={() => void save()}
      secondary={yard && (
        <Button variant="danger" className="h-[34px]" disabled={busy} onClick={() => void retire()}>
          Retire yard
        </Button>
      )}
      primary={(
        <Button variant="primary" className="h-[34px]" disabled={busy} onClick={() => void save()}>
          {yard ? 'Save yard' : 'Add yard'}
        </Button>
      )}
    >
      <div className="flex flex-col gap-3">
        <Field label="Name" required>
          {({ id }) => (
            <TextInput id={id} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>

        <AddressFields value={address} onChange={setAddress} suite={false} />

        <SwitchRow
          label="Default yard"
          sub="new crews start here, and stock comes off here unless a visit says otherwise"
          checked={isDefault}
          onChange={setDefault}
        />

        <Field label="Note" hint="what the crew needs to know — the gate code, which side to load on">
          {({ id }) => (
            <TextArea id={id} rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
          )}
        </Field>

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </Sheet>
  )
}

function ZoneSheet({ unitId, zone, onClose, onDone }: {
  unitId: string
  zone: ZoneView | null
  onClose: () => void
  onDone: (items: ZoneView[]) => void
}) {
  const [name, setName] = useState(zone?.name ?? '')
  const [colour, setColour] = useState(zone?.colour ?? ZONE_COLOURS[0]!.value)
  const [active, setActive] = useState(zone?.active ?? true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const { items } = await request<{ items: ZoneView[] }>(
        zone ? `/org-units/${unitId}/zones/${zone.id}` : `/org-units/${unitId}/zones`,
        {
          method: zone ? 'PUT' : 'POST',
          body: { name, colour, active, sortOrder: zone?.sortOrder ?? 0 },
        })
      onDone(items)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  async function retire() {
    if (!zone) return
    setBusy(true)
    setError(null)
    try {
      const { items } = await request<{ items: ZoneView[] }>(
        `/org-units/${unitId}/zones/${zone.id}`, { method: 'DELETE' })
      onDone(items)
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That zone stays.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      kind="zone"
      title={zone ? zone.name : 'New service zone'}
      sub="group properties for routing and the schedule board"
      width={480}
      dirty
      saveLabel={zone ? 'Save zone' : 'Add zone'}
      onSave={() => void save()}
      secondary={zone && (
        <Button variant="danger" className="h-[34px]" disabled={busy} onClick={() => void retire()}>
          Retire zone
        </Button>
      )}
      primary={(
        <Button variant="primary" className="h-[34px]" disabled={busy} onClick={() => void save()}>
          {zone ? 'Save zone' : 'Add zone'}
        </Button>
      )}
    >
      <div className="flex flex-col gap-3">
        <Field label="Name" required>
          {({ id }) => (
            <TextInput id={id} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>

        <Field label="Colour" group hint="what it is on the board and in the crew app">
          {({ labelId }) => (
            <SwatchField
              label="Colour"
              labelledBy={labelId}
              value={colour}
              onChange={setColour}
              options={ZONE_COLOURS.map((one) => ({
                value: one.value, css: one.value, label: one.label,
              }))}
            />
          )}
        </Field>

        <SwitchRow
          label="Offered"
          sub="a zone switched off keeps its properties and stops being a choice"
          checked={active}
          onChange={setActive}
        />

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </Sheet>
  )
}
